/**
 * This file is part of persistore which is released under MIT license.
 *
 * The LICENSE file can be found in the root directory of this project.
 *
 */
const variables = {
    store: {},
    prefix: '',
};
const Access = {
    variables: () => variables,
    storage: (local) => (local ? window.localStorage : window.sessionStorage),
    document: () => window.document,
};

/**
 * This file is part of persistore which is released under MIT license.
 *
 * The LICENSE file can be found in the root directory of this project.
 *
 */
// it is very important that deleting and setting cookies is performed
// on the same cookie location
const cookieLocation = (sameSite = 'strict') => `${Access.variables().ci ? '' : 'Secure;'}Path=/;SameSite=${sameSite}`;
const MAX_COOKIE_LENGTH = 4093;
const set = (name, value, sameSite) => {
    const encodedValue = encodeURIComponent(value);
    const cookie = `${name}=${encodedValue};${cookieLocation(sameSite)}`;
    if (cookie.length > MAX_COOKIE_LENGTH)
        throw new Error(`Unable to set cookie. Cookie string is to long (${cookie.length} > ${MAX_COOKIE_LENGTH}).`);
    Access.document().cookie = cookie;
};
const remove = (name) => {
    Access.document().cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT;${cookieLocation()}`;
};
const getAll = () => {
    const current = Access.document().cookie;
    return current
        ? current.split(';').map((cookie) => {
            const split = cookie.split('=');
            return [split[0].trim(), decodeURIComponent(split[1])];
        })
        : [];
};
const get = (name) => {
    const foundCookie = getAll().filter((cookiePair) => cookiePair[0] === name);
    if (foundCookie.length > 0)
        return foundCookie[0][1];
    return undefined;
};
const CookieUtil = { set, remove, get, getAll };

/**
 * This file is part of persistore which is released under MIT license.
 *
 * The LICENSE file can be found in the root directory of this project.
 *
 */
const useStorage = (local) => {
    const lv = Access.variables();
    const isAvailable = local ? 'lsa' : 'ssa';
    if (lv[isAvailable] === undefined) {
        try {
            const storage = Access.storage(local);
            const x = '__test__';
            storage.setItem(x, x);
            storage.removeItem(x);
            lv[isAvailable] = true;
        }
        catch (e) {
            lv[isAvailable] = false;
        }
    }
    return lv[isAvailable];
};
const useCookies = () => {
    const lv = Access.variables();
    if (lv.ca === undefined) {
        try {
            const document = Access.document();
            const x = '__test__';
            CookieUtil.set(x, x);
            lv.ca = document.cookie.indexOf(x) !== -1;
            CookieUtil.remove(x);
            lv.ca = lv.ca && document.cookie.indexOf(x) === -1;
        }
        catch (e) {
            lv.ca = false;
        }
    }
    return lv.ca;
};
const _prefixed = (name) => Access.variables().prefix + name;
const _set = (local) => (name, value) => {
    const key = _prefixed(name);
    if (useStorage(local))
        return Access.storage(local).setItem(key, value);
    if (useCookies())
        return CookieUtil.set(key, value);
    Access.variables().store[key] = value;
};
const _get = (local) => (name) => {
    const key = _prefixed(name);
    if (useStorage(local))
        return Access.storage(local).getItem(key) || undefined;
    if (useCookies())
        return CookieUtil.get(key);
    return Access.variables().store[key];
};
const _remove = (local) => (name) => {
    const key = _prefixed(name);
    if (useStorage(local))
        return Access.storage(local).removeItem(key);
    if (useCookies())
        return CookieUtil.remove(key);
    delete Access.variables().store[key];
};
const config = ({ prefix, insecure }) => {
    prefix !== undefined && (Access.variables().prefix = prefix);
    insecure !== undefined && (Access.variables().ci = insecure);
};
const Persistore = {
    set: _set(true),
    get: _get(true),
    remove: _remove(true),
    session: {
        set: _set(false),
        get: _get(false),
        remove: _remove(false),
    },
    config,
};

export { CookieUtil, Persistore, useCookies, useStorage };
